import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Save, Plus, Trash2, Edit, X, Upload, Loader2, Eye, EyeOff, GripVertical,
  Users, GraduationCap, Quote, HelpCircle, Building2, Award, Tag, Settings,
  FileText, Briefcase, Image as ImageIcon, ExternalLink, Mail, CheckCircle,
  ChevronUp, ChevronDown,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const mediaUrl = (u) => (!u ? '' : (u.startsWith('http') ? u : `${BACKEND_URL}${u}`));

const TABS = [
  { id: 'hero', label: 'Hero & About', icon: FileText },
  { id: 'tracks', label: 'Tracks & Syllabus', icon: GraduationCap },
  { id: 'immersion', label: 'Immersion & Certificate', icon: Award },
  { id: 'pricing', label: 'Pricing & Coupons', icon: Tag },
  { id: 'benefits', label: 'Benefits & Timeline', icon: Briefcase },
  { id: 'mentors', label: 'Mentors', icon: Users },
  { id: 'lecturers', label: 'Guest Lectures', icon: Users },
  { id: 'testimonials', label: 'Testimonials', icon: Quote },
  { id: 'partners', label: 'Startup Partners', icon: Building2 },
  { id: 'faqs', label: 'FAQs', icon: HelpCircle },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'applications', label: 'Applications', icon: Mail },
  { id: 'enrollments', label: 'Enrollments', icon: CheckCircle },
];

const inputCls = 'w-full px-3 py-2 text-sm bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-pink-500 outline-none';
const labelCls = 'block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wide';
const cardCls = 'bg-slate-800 border border-slate-700 rounded-xl p-5';

const FellowshipAdmin = ({ token }) => {
  const [tab, setTab] = useState('hero');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  return (
    <div className="space-y-5 min-w-0">
      <div>
        <h2 className="text-xl font-bold text-white">EvalentX Fellowship</h2>
        <p className="text-sm text-slate-400 mt-0.5">Manage every section of the public Fellowship page. Empty lists hide automatically on the live page.</p>
        <a href="/fellowship" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-pink-400 hover:underline mt-1" data-testid="fellowship-view-live">
          <ExternalLink size={12} /> View live page
        </a>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`fellowship-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${tab === t.id ? 'bg-pink-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {['hero', 'tracks', 'immersion', 'pricing', 'benefits', 'settings'].includes(tab) && <ConfigEditor tab={tab} auth={auth} token={token} />}
      {tab === 'mentors' && <ListManager resource="mentors" auth={auth} token={token} fields={MENTOR_FIELDS} title="Mentors" reorderable />}
      {tab === 'lecturers' && <ListManager resource="guest-lecturers" auth={auth} token={token} fields={LECTURER_FIELDS} title="Guest Lecturers" reorderable />}
      {tab === 'testimonials' && <ListManager resource="testimonials" auth={auth} token={token} fields={TESTIMONIAL_FIELDS} title="Testimonials" />}
      {tab === 'partners' && <ListManager resource="partners" auth={auth} token={token} fields={PARTNER_FIELDS} title="Startup Partners" reorderable />}
      {tab === 'faqs' && <ListManager resource="faqs" auth={auth} token={token} fields={FAQ_FIELDS} title="FAQs" />}
      {tab === 'applications' && <DataTable endpoint="data/applications" auth={auth} kind="applications" />}
      {tab === 'enrollments' && <DataTable endpoint="data/enrollments" auth={auth} kind="enrollments" />}
    </div>
  );
};

// ==================== Image Upload ====================
const ImageUpload = ({ value, onChange, token, label = 'Image', testid }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/fellowship/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
      onChange(res.data.url);
      toast.success('Uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative">
            <img src={mediaUrl(value)} alt="preview" className="w-16 h-16 object-contain bg-slate-900 border border-slate-600 rounded-lg" />
            <button onClick={() => onChange('')} className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center text-white"><X size={11} /></button>
          </div>
        ) : (
          <div className="w-16 h-16 bg-slate-900 border border-dashed border-slate-600 rounded-lg flex items-center justify-center"><ImageIcon size={20} className="text-slate-600" /></div>
        )}
        <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={testid}
          className="flex items-center gap-1.5 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white text-xs font-semibold rounded-lg disabled:opacity-60">
          {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {value ? 'Replace' : 'Upload'}
        </button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handle} />
      </div>
    </div>
  );
};

// ==================== Config Editor ====================
const ConfigEditor = ({ tab, auth, token }) => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await axios.get(`${API}/admin/fellowship/config`, auth);
    setCfg(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/fellowship/config`, cfg, auth);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const set = (path, val) => {
    setCfg((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let o = next;
      for (let i = 0; i < path.length - 1; i++) o = o[path[i]];
      o[path[path.length - 1]] = val;
      return next;
    });
  };

  if (!cfg) return <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 size={16} className="animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-4">
      {tab === 'hero' && <HeroAbout cfg={cfg} set={set} token={token} />}
      {tab === 'tracks' && <TracksSyllabus cfg={cfg} set={set} setCfg={setCfg} />}
      {tab === 'immersion' && <ImmersionCert cfg={cfg} set={set} token={token} />}
      {tab === 'pricing' && <PricingCoupons cfg={cfg} set={set} setCfg={setCfg} />}
      {tab === 'benefits' && <BenefitsTimeline cfg={cfg} setCfg={setCfg} />}
      {tab === 'settings' && <SettingsTab cfg={cfg} set={set} token={token} />}

      <button onClick={save} disabled={saving} data-testid="fellowship-save-config"
        className="flex items-center gap-2 px-5 py-2.5 bg-pink-600 hover:bg-pink-500 text-white text-sm font-bold rounded-lg disabled:opacity-60 sticky bottom-4">
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Changes
      </button>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', textarea, placeholder, testid }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {textarea
      ? <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={placeholder} data-testid={testid} className={inputCls} />
      : <input type={type} value={value ?? ''} onChange={(e) => onChange(type === 'number' ? Number(e.target.value) : e.target.value)} placeholder={placeholder} data-testid={testid} className={inputCls} />}
  </div>
);

const HeroAbout = ({ cfg, set, token }) => (
  <>
    <div className={cardCls}>
      <h3 className="text-sm font-bold text-white mb-4">Hero Section</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Badge" value={cfg.hero.badge} onChange={(v) => set(['hero', 'badge'], v)} testid="hero-badge" />
        <Field label="Immersion Highlight" value={cfg.hero.immersion_highlight} onChange={(v) => set(['hero', 'immersion_highlight'], v)} />
        <Field label="Title Line 1" value={cfg.hero.title_line1} onChange={(v) => set(['hero', 'title_line1'], v)} />
        <Field label="Title Line 2" value={cfg.hero.title_line2} onChange={(v) => set(['hero', 'title_line2'], v)} />
        <Field label="Duration Label" value={cfg.hero.duration_label} onChange={(v) => set(['hero', 'duration_label'], v)} />
        <Field label="Duration Sub" value={cfg.hero.duration_sub} onChange={(v) => set(['hero', 'duration_sub'], v)} />
        <Field label="Primary CTA" value={cfg.hero.primary_cta} onChange={(v) => set(['hero', 'primary_cta'], v)} />
        <Field label="Secondary CTA" value={cfg.hero.secondary_cta} onChange={(v) => set(['hero', 'secondary_cta'], v)} />
      </div>
      <div className="mt-3"><Field label="Tagline" value={cfg.hero.tagline} onChange={(v) => set(['hero', 'tagline'], v)} textarea testid="hero-tagline" /></div>
    </div>
    <div className={cardCls}>
      <h3 className="text-sm font-bold text-white mb-4">About Section</h3>
      <Field label="Heading" value={cfg.about.heading} onChange={(v) => set(['about', 'heading'], v)} />
      <div className="mt-3"><Field label="Description" value={cfg.about.description} onChange={(v) => set(['about', 'description'], v)} textarea /></div>
      <p className="text-xs text-slate-400 mt-4 mb-2 uppercase tracking-wide font-semibold">Pillars</p>
      <ArrayEditor items={cfg.about.pillars || []} onChange={(v) => set(['about', 'pillars'], v)} template={{ title: '', description: '' }}
        renderItem={(it, upd) => (<div className="grid sm:grid-cols-2 gap-2 flex-1"><input className={inputCls} placeholder="Title" value={it.title} onChange={(e) => upd({ ...it, title: e.target.value })} /><input className={inputCls} placeholder="Description" value={it.description} onChange={(e) => upd({ ...it, description: e.target.value })} /></div>)} />
    </div>
  </>
);

const TracksSyllabus = ({ cfg, set, setCfg }) => (
  <>
    <div className={cardCls}>
      <h3 className="text-sm font-bold text-white mb-4">Fellowship Tracks</h3>
      <ArrayEditor items={cfg.tracks || []} onChange={(v) => set(['tracks'], v)} template={{ id: '', name: '', overview: '', tools: [], outcomes: [] }}
        renderItem={(it, upd) => (
          <div className="space-y-2 flex-1">
            <div className="grid sm:grid-cols-2 gap-2">
              <input className={inputCls} placeholder="ID (e.g. ai)" value={it.id} onChange={(e) => upd({ ...it, id: e.target.value })} />
              <input className={inputCls} placeholder="Name" value={it.name} onChange={(e) => upd({ ...it, name: e.target.value })} />
            </div>
            <textarea className={inputCls} rows={2} placeholder="Overview" value={it.overview} onChange={(e) => upd({ ...it, overview: e.target.value })} />
            <input className={inputCls} placeholder="Tools (comma separated)" value={(it.tools || []).join(', ')} onChange={(e) => upd({ ...it, tools: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
            <input className={inputCls} placeholder="Career outcomes (comma separated)" value={(it.outcomes || []).join(', ')} onChange={(e) => upd({ ...it, outcomes: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
          </div>
        )} />
    </div>
    <div className={cardCls}>
      <h3 className="text-sm font-bold text-white mb-1">Syllabus</h3>
      <p className="text-xs text-slate-400 mb-4">Edit modules per track. Track key must match a Track ID above.</p>
      {Object.keys(cfg.syllabus || {}).map((key) => (
        <div key={key} className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-bold text-pink-400 uppercase tracking-wide">{key}</span>
            <button onClick={() => setCfg((p) => { const n = JSON.parse(JSON.stringify(p)); delete n.syllabus[key]; return n; })} className="text-red-400 text-xs hover:underline">Remove track</button>
          </div>
          <textarea className={inputCls} rows={4} value={(cfg.syllabus[key] || []).join('\n')} placeholder="One module per line"
            onChange={(e) => set(['syllabus', key], e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} />
        </div>
      ))}
      <AddKeyRow onAdd={(k) => set(['syllabus', k], [])} />
    </div>
  </>
);

const AddKeyRow = ({ onAdd }) => {
  const [k, setK] = useState('');
  return (
    <div className="flex gap-2">
      <input className={inputCls} placeholder="New track key (e.g. ai)" value={k} onChange={(e) => setK(e.target.value)} />
      <button onClick={() => { if (k.trim()) { onAdd(k.trim()); setK(''); } }} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white text-xs font-semibold rounded-lg whitespace-nowrap">Add Track Key</button>
    </div>
  );
};

const ImmersionCert = ({ cfg, set, token }) => (
  <>
    <div className={cardCls}>
      <h3 className="text-sm font-bold text-white mb-4">Startup Immersion</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Heading" value={cfg.startup_immersion.heading} onChange={(v) => set(['startup_immersion', 'heading'], v)} />
        <Field label="Partner Name" value={cfg.startup_immersion.partner_name} onChange={(v) => set(['startup_immersion', 'partner_name'], v)} />
        <Field label="Partner Tagline" value={cfg.startup_immersion.partner_tagline} onChange={(v) => set(['startup_immersion', 'partner_tagline'], v)} />
      </div>
      <div className="mt-3"><Field label="Description" value={cfg.startup_immersion.description} onChange={(v) => set(['startup_immersion', 'description'], v)} textarea /></div>
      <div className="mt-3"><Field label="Points (one per line)" value={(cfg.startup_immersion.points || []).join('\n')} onChange={(v) => set(['startup_immersion', 'points'], v.split('\n').map((s) => s.trim()).filter(Boolean))} textarea /></div>
      <div className="mt-3">
        <ImageUpload label="Partner Logo (e.g. MedhiQa — upload official asset only)" value={cfg.startup_immersion.partner_logo_url} onChange={(v) => set(['startup_immersion', 'partner_logo_url'], v)} token={token} testid="immersion-logo-upload" />
        <p className="text-[11px] text-slate-500 mt-1">Logo appears on the page only after upload. Until then the section stays text-only.</p>
      </div>
    </div>
    <div className={cardCls}>
      <h3 className="text-sm font-bold text-white mb-4">Certification & Letter of Appreciation</h3>
      <Field label="Heading" value={cfg.certificate.heading} onChange={(v) => set(['certificate', 'heading'], v)} />
      <div className="mt-3"><Field label="Description" value={cfg.certificate.description} onChange={(v) => set(['certificate', 'description'], v)} textarea /></div>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <ImageUpload label="EvalentX Logo" value={cfg.certificate.evalentx_logo_url} onChange={(v) => set(['certificate', 'evalentx_logo_url'], v)} token={token} />
        <ImageUpload label="Partner Logo" value={cfg.certificate.partner_logo_url} onChange={(v) => set(['certificate', 'partner_logo_url'], v)} token={token} />
        <ImageUpload label="Certificate Preview" value={cfg.certificate.certificate_preview_url} onChange={(v) => set(['certificate', 'certificate_preview_url'], v)} token={token} />
        <ImageUpload label="Letter Preview" value={cfg.certificate.letter_preview_url} onChange={(v) => set(['certificate', 'letter_preview_url'], v)} token={token} />
      </div>
    </div>
  </>
);

const PricingCoupons = ({ cfg, set }) => (
  <>
    <div className={cardCls}>
      <h3 className="text-sm font-bold text-white mb-4">Pricing</h3>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Price (INR)" type="number" value={cfg.pricing.price} onChange={(v) => set(['pricing', 'price'], v)} testid="pricing-price" />
        <Field label="Original Price (optional)" type="number" value={cfg.pricing.original_price} onChange={(v) => set(['pricing', 'original_price'], v)} />
        <Field label="Note" value={cfg.pricing.note} onChange={(v) => set(['pricing', 'note'], v)} />
      </div>
    </div>
    <div className={cardCls}>
      <h3 className="text-sm font-bold text-white mb-4">Discount Codes</h3>
      <ArrayEditor items={cfg.pricing.coupons || []} onChange={(v) => set(['pricing', 'coupons'], v)} template={{ code: '', discount_type: 'percentage', discount_value: 0, is_active: true }}
        renderItem={(it, upd) => (
          <div className="grid sm:grid-cols-4 gap-2 flex-1 items-center">
            <input className={inputCls} placeholder="CODE" value={it.code} onChange={(e) => upd({ ...it, code: e.target.value.toUpperCase() })} />
            <select className={inputCls} value={it.discount_type} onChange={(e) => upd({ ...it, discount_type: e.target.value })}>
              <option value="percentage">Percentage %</option>
              <option value="fixed">Fixed ₹</option>
            </select>
            <input className={inputCls} type="number" placeholder="Value" value={it.discount_value} onChange={(e) => upd({ ...it, discount_value: Number(e.target.value) })} />
            <label className="flex items-center gap-2 text-xs text-slate-300"><input type="checkbox" checked={it.is_active} onChange={(e) => upd({ ...it, is_active: e.target.checked })} /> Active</label>
          </div>
        )} />
    </div>
  </>
);

const BenefitsTimeline = ({ cfg, setCfg }) => {
  const setArr = (key, v) => setCfg((p) => ({ ...p, [key]: v }));
  return (
    <>
      <div className={cardCls}>
        <h3 className="text-sm font-bold text-white mb-4">Benefits</h3>
        <ArrayEditor items={cfg.benefits || []} onChange={(v) => setArr('benefits', v)} template={{ title: '', description: '' }}
          renderItem={(it, upd) => (<div className="grid sm:grid-cols-2 gap-2 flex-1"><input className={inputCls} placeholder="Title" value={it.title} onChange={(e) => upd({ ...it, title: e.target.value })} /><input className={inputCls} placeholder="Description" value={it.description} onChange={(e) => upd({ ...it, description: e.target.value })} /></div>)} />
      </div>
      <div className={cardCls}>
        <h3 className="text-sm font-bold text-white mb-4">Timeline</h3>
        <ArrayEditor items={cfg.timeline || []} onChange={(v) => setArr('timeline', v)} template={{ title: '', description: '' }}
          renderItem={(it, upd) => (<div className="grid sm:grid-cols-2 gap-2 flex-1"><input className={inputCls} placeholder="Step title" value={it.title} onChange={(e) => upd({ ...it, title: e.target.value })} /><input className={inputCls} placeholder="Description" value={it.description} onChange={(e) => upd({ ...it, description: e.target.value })} /></div>)} />
      </div>
    </>
  );
};

const SettingsTab = ({ cfg, set, token }) => (
  <>
    <div className={cardCls}>
      <h3 className="text-sm font-bold text-white mb-4">WhatsApp Inquiry CTA</h3>
      <label className="flex items-center gap-2 text-sm text-slate-300 mb-3"><input type="checkbox" checked={cfg.whatsapp.enabled} onChange={(e) => set(['whatsapp', 'enabled'], e.target.checked)} data-testid="whatsapp-enabled" /> Enable WhatsApp button</label>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="WhatsApp Number (with country code)" value={cfg.whatsapp.number} onChange={(v) => set(['whatsapp', 'number'], v)} placeholder="+919999999999" testid="whatsapp-number" />
        <Field label="Pre-filled Message" value={cfg.whatsapp.prefill_message} onChange={(v) => set(['whatsapp', 'prefill_message'], v)} />
      </div>
      <p className="text-[11px] text-slate-500 mt-2">CTA hides automatically if disabled or no number is set.</p>
    </div>
    <div className={cardCls}>
      <h3 className="text-sm font-bold text-white mb-4">Announcement Banner</h3>
      <label className="flex items-center gap-2 text-sm text-slate-300 mb-3"><input type="checkbox" checked={cfg.banner?.enabled} onChange={(e) => set(['banner', 'enabled'], e.target.checked)} /> Enable banner</label>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Banner Text" value={cfg.banner?.text} onChange={(v) => set(['banner', 'text'], v)} />
        <Field label="Banner Link (optional)" value={cfg.banner?.link} onChange={(v) => set(['banner', 'link'], v)} />
      </div>
    </div>
    <div className={cardCls}>
      <h3 className="text-sm font-bold text-white mb-4">SEO & Open Graph</h3>
      <div className="grid sm:grid-cols-1 gap-3">
        <Field label="Meta Title" value={cfg.seo.title} onChange={(v) => set(['seo', 'title'], v)} />
        <Field label="Meta Description" value={cfg.seo.description} onChange={(v) => set(['seo', 'description'], v)} textarea />
        <ImageUpload label="OG Image" value={cfg.seo.og_image} onChange={(v) => set(['seo', 'og_image'], v)} token={token} />
      </div>
    </div>
    <div className={cardCls}>
      <h3 className="text-sm font-bold text-white mb-4">Section Visibility</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {Object.keys(cfg.sections_visibility || {}).map((k) => (
          <label key={k} className="flex items-center gap-2 text-sm text-slate-300 capitalize bg-slate-900 px-3 py-2 rounded-lg">
            <input type="checkbox" checked={cfg.sections_visibility[k]} onChange={(e) => set(['sections_visibility', k], e.target.checked)} data-testid={`visibility-${k}`} />
            {k.replace(/_/g, ' ')}
          </label>
        ))}
      </div>
    </div>
  </>
);

// Generic array editor (add/remove/reorder by simple list)
const ArrayEditor = ({ items, onChange, template, renderItem }) => {
  const upd = (idx, val) => onChange(items.map((it, i) => (i === idx ? val : it)));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, JSON.parse(JSON.stringify(template))]);
  return (
    <div className="space-y-2">
      {items.map((it, idx) => (
        <div key={idx} className="flex items-start gap-2 bg-slate-900 p-2.5 rounded-lg">
          <GripVertical size={14} className="text-slate-600 mt-2 shrink-0" />
          {renderItem(it, (v) => upd(idx, v))}
          <button onClick={() => remove(idx)} className="text-red-400 hover:text-red-300 mt-1.5 shrink-0"><Trash2 size={15} /></button>
        </div>
      ))}
      <button onClick={add} className="flex items-center gap-1.5 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white text-xs font-semibold rounded-lg"><Plus size={14} /> Add</button>
    </div>
  );
};

// ==================== List Manager (CRUD resources) ====================
const MENTOR_FIELDS = [
  { key: 'name', label: 'Mentor Name', required: true },
  { key: 'designation', label: 'Designation' },
  { key: 'company', label: 'Company' },
  { key: 'image_url', label: 'Profile Image', image: true },
  { key: 'bio', label: 'Bio / About', textarea: true },
  { key: 'skills', label: 'Skills / Expertise (comma separated)', placeholder: 'Machine Learning, LLMs, Python' },
  { key: 'linkedin', label: 'LinkedIn URL', placeholder: 'https://linkedin.com/in/...' },
];
const LECTURER_FIELDS = [
  { key: 'name', label: 'Guest Name', required: true },
  { key: 'designation', label: 'Designation' },
  { key: 'company', label: 'Company' },
  { key: 'image_url', label: 'Profile Photo', image: true },
  { key: 'session_topic', label: 'Session Topic', placeholder: 'e.g. Scaling LLMs in production' },
  { key: 'bio', label: 'Description / Bio', textarea: true },
  { key: 'linkedin', label: 'LinkedIn / Social URL', placeholder: 'https://linkedin.com/in/...' },
];
const TESTIMONIAL_FIELDS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'role', label: 'Role' },
  { key: 'company', label: 'Company' },
  { key: 'text', label: 'Testimonial', textarea: true, required: true },
  { key: 'rating', label: 'Rating (1-5)', type: 'number' },
  { key: 'image_url', label: 'Photo', image: true },
];
const PARTNER_FIELDS = [
  { key: 'name', label: 'Startup / Partner Name', required: true },
  { key: 'logo_url', label: 'Startup Logo', image: true },
  { key: 'description', label: 'Description', textarea: true },
  { key: 'website', label: 'Website / Social URL', placeholder: 'https://...' },
];
const FAQ_FIELDS = [
  { key: 'question', label: 'Question', required: true },
  { key: 'answer', label: 'Answer', textarea: true, required: true },
];

const ListManager = ({ resource, auth, token, fields, title, reorderable }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/fellowship/${resource}`, auth);
      setItems(res.data.items || []);
    } finally { setLoading(false); }
  }, [resource]);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (editing?.id) await axios.put(`${API}/admin/fellowship/${resource}/${editing.id}`, form, auth);
      else await axios.post(`${API}/admin/fellowship/${resource}`, form, auth);
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await axios.delete(`${API}/admin/fellowship/${resource}/${id}`, auth);
    toast.success('Deleted');
    load();
  };
  const toggleActive = async (it) => {
    await axios.put(`${API}/admin/fellowship/${resource}/${it.id}`, { is_active: !it.is_active }, auth);
    load();
  };
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr); // optimistic UI
    setReordering(true);
    try {
      await Promise.all(arr.map((it, k) => axios.put(`${API}/admin/fellowship/${resource}/${it.id}`, { order: k }, auth)));
      toast.success('Order updated');
    } catch (err) {
      toast.error('Reorder failed');
      load();
    } finally { setReordering(false); }
  };

  return (
    <div className={cardCls}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-white">{title}</h3>
          <p className="text-xs text-slate-400">{items.length} item(s). Section hides on the live page when none are active.</p>
        </div>
        <button onClick={() => setEditing({})} data-testid={`${resource}-add-btn`} className="flex items-center gap-1.5 px-3 py-2 bg-pink-600 hover:bg-pink-500 text-white text-xs font-bold rounded-lg"><Plus size={14} /> Add</button>
      </div>

      {loading ? <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Loading…</div> : (
        items.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">No items yet. Click "Add" to create genuine content.</p> : (
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div key={it.id} className="flex items-center gap-3 bg-slate-900 p-3 rounded-lg" data-testid={`${resource}-item`}>
                {reorderable && (
                  <div className="flex flex-col -my-1">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0 || reordering} data-testid={`${resource}-move-up-${idx}`}
                      className="text-slate-500 hover:text-white disabled:opacity-30 disabled:hover:text-slate-500"><ChevronUp size={15} /></button>
                    <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1 || reordering} data-testid={`${resource}-move-down-${idx}`}
                      className="text-slate-500 hover:text-white disabled:opacity-30 disabled:hover:text-slate-500"><ChevronDown size={15} /></button>
                  </div>
                )}
                {fields.find((f) => f.image) && (
                  it[fields.find((f) => f.image).key]
                    ? <img src={mediaUrl(it[fields.find((f) => f.image).key])} alt="" className="w-10 h-10 rounded object-contain bg-slate-800" />
                    : <div className="w-10 h-10 rounded bg-slate-800 flex items-center justify-center"><ImageIcon size={16} className="text-slate-600" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{it.name || it.question}</p>
                  <p className="text-xs text-slate-400 truncate">{it.designation || it.session_topic || it.role || it.answer || it.description || it.company}</p>
                </div>
                <button onClick={() => toggleActive(it)} title={it.is_active ? 'Active' : 'Hidden'} className={it.is_active ? 'text-emerald-400' : 'text-slate-500'}>{it.is_active ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                <button onClick={() => setEditing(it)} className="text-slate-300 hover:text-white"><Edit size={15} /></button>
                <button onClick={() => del(it.id)} className="text-red-400 hover:text-red-300"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )
      )}

      {editing !== null && <ItemModal fields={fields} item={editing} onClose={() => setEditing(null)} onSave={save} token={token} resource={resource} />}
    </div>
  );
};

const ItemModal = ({ fields, item, onClose, onSave, token, resource }) => {
  const [form, setForm] = useState(() => {
    const base = { is_active: true };
    fields.forEach((f) => { base[f.key] = item[f.key] ?? (f.type === 'number' ? 0 : ''); });
    if ('is_active' in item) base.is_active = item.is_active;
    return base;
  });
  const [err, setErr] = useState('');
  const submit = () => {
    for (const f of fields) {
      if (f.required && !String(form[f.key] || '').trim()) { setErr(`${f.label} is required`); return; }
    }
    onSave(form);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid={`${resource}-modal`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-white">{item.id ? 'Edit' : 'Add'} Item</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          {fields.map((f) => (
            f.image
              ? <ImageUpload key={f.key} label={f.label} value={form[f.key]} onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))} token={token} testid={`${resource}-${f.key}-upload`} />
              : <Field key={f.key} label={f.label + (f.required ? ' *' : '')} value={form[f.key]} type={f.type || 'text'} textarea={f.textarea}
                  onChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))} testid={`${resource}-${f.key}`} />
          ))}
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} /> Active (visible on page)</label>
          {err && <p className="text-sm text-red-400">{err}</p>}
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={submit} data-testid={`${resource}-save`} className="flex-1 py-2.5 bg-pink-600 hover:bg-pink-500 text-white text-sm font-bold rounded-lg">Save</button>
          <button onClick={onClose} className="px-4 py-2.5 bg-slate-700 text-white text-sm font-semibold rounded-lg">Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ==================== Data Table (Applications / Enrollments) ====================
const DataTable = ({ endpoint, auth, kind }) => {
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/admin/fellowship/${endpoint}`, auth); setData(res.data); }
      finally { setLoading(false); }
    })();
  }, [endpoint]);

  if (loading) return <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Loading…</div>;
  const items = data.items || [];

  return (
    <div className={cardCls}>
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <Stat label="Total" value={data.total ?? items.length} />
        {kind === 'enrollments' && <><Stat label="Confirmed" value={data.confirmed ?? 0} /><Stat label="Revenue" value={`₹${(data.revenue || 0).toLocaleString('en-IN')}`} /></>}
      </div>
      {items.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">No {kind} yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-400 border-b border-slate-700">
              <th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Email</th><th className="py-2 pr-4">Phone</th>
              <th className="py-2 pr-4">Track</th><th className="py-2 pr-4">Mode</th>
              {kind === 'enrollments' ? <><th className="py-2 pr-4">Amount</th><th className="py-2 pr-4">Status</th></> : <th className="py-2 pr-4">Org</th>}
              <th className="py-2">Date</th>
            </tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-slate-800 text-slate-200" data-testid={`${kind}-row`}>
                  <td className="py-2 pr-4 font-semibold">{it.full_name}</td>
                  <td className="py-2 pr-4">{it.email}</td>
                  <td className="py-2 pr-4">{it.phone}</td>
                  <td className="py-2 pr-4">{it.track}</td>
                  <td className="py-2 pr-4">{it.mode}</td>
                  {kind === 'enrollments'
                    ? <><td className="py-2 pr-4">₹{(it.amount || 0).toLocaleString('en-IN')}</td><td className="py-2 pr-4"><span className={`px-2 py-0.5 rounded text-xs ${it.status === 'confirmed' ? 'bg-emerald-900 text-emerald-300' : 'bg-slate-700 text-slate-300'}`}>{it.status}</span></td></>
                    : <td className="py-2 pr-4">{it.organization}</td>}
                  <td className="py-2 text-slate-400">{new Date(it.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-slate-900 px-4 py-2.5 rounded-lg">
    <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
    <p className="text-lg font-bold text-white">{value}</p>
  </div>
);

export default FellowshipAdmin;
